<script setup lang="ts">
import { computed } from 'vue';

export interface Props {
  modelValue: boolean;
  label?: string;
  disabled?: boolean;
  dataCy?: string;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  dataCy: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isChecked = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value);
  },
});
</script>

<template>
  <label class="checkbox-label" :class="{ disabled }">
    <input type="checkbox" class="custom-checkbox" v-model="isChecked" :disabled="disabled" :data-cy="dataCy" />
    <span class="checkbox-text">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<style scoped>
.checkbox-label {
  color: var(--primary-text-color);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.checkbox-text {
  font-weight: 400;
}

.custom-checkbox {
  appearance: none;
  height: 22px;
  width: 22px;
  background-color: var(--primary-bg-color);
  border: 1px solid var(--primary-text-color);
  border-radius: 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease-in-out;
  position: relative;
  flex-shrink: 0;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: #94a3b8;
  }

  &:checked {
    background-color: var(--primary-bg-color);
    border-color: var(--primary-text-color);
  }

  &:checked::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    background-color: var(--secondary-bg-color);
    border-radius: 1px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

@media (max-width: 480px) {
  .checkbox-label {
    gap: 0.5rem;
  }

  .custom-checkbox {
    height: 20px;
    width: 20px;

    &:checked::after {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
